import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiInterfaces } from '@flashbacktech/flashbackclient/api';
import { toastError } from '../utils/toast';
import { useWithTokenRefresh } from '../utils/api';
import { useApi } from '../contexts/ApiContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { handleGoogleSignIn, handleMicrosoftSignIn, handleGithubSignIn, setAuthState } = useAuth();
  const withTokenRefresh = useWithTokenRefresh();
  const { apiClient } = useApi();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNoAccountModal, setShowNoAccountModal] = useState(false);


  useEffect(() => {
    const activationUid = localStorage.getItem('activation_uid');
    const activationToken = localStorage.getItem('activation_token');
    const signupEmail = localStorage.getItem('signupEmail');
    localStorage.clear();
    if (activationUid) localStorage.setItem('activation_uid', activationUid);
    if (activationToken) localStorage.setItem('activation_token', activationToken);
    if (signupEmail) localStorage.setItem('signupEmail', signupEmail);
  }, []);

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await withTokenRefresh(
        async () => await apiClient.userLogin({ email, password })
      );
      if (response.accessToken && response.refreshToken && response.user) {
        // If backend indicates MFA is required, set a pending flag
        // so loadGlobalData skips until MFA verification is complete.
        // PostLoginMFAWrapper will intercept and show the MFA screen.
        if ((response as any).mfaRequired === true) {
          try { sessionStorage.setItem('mfa_pending', 'true'); } catch { /* ignore storage errors */ }
        }
        completeLogin(response);
      } else {
        toastError(response.message || 'Login failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error) || 'Login failed';
      // Check if error indicates user doesn't have an account
      if (errorMessage.toLowerCase().includes('not found') || 
          errorMessage.toLowerCase().includes('does not exist') ||
          errorMessage.toLowerCase().includes('no user') ||
          errorMessage.toLowerCase().includes('invalid credentials') ||
          errorMessage.toLowerCase().includes('user not found')) {
        setShowNoAccountModal(true);
      } else {
        toastError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (response: any) => {
    setAuthState({
      user: {
        email: response.user.email,
        name: response.user.name,
        imageUrl: '',
      },
      token: response.accessToken,
      accessToken: response.tokenId || null,
      refreshToken: response.refreshToken || null,
      expiresAt: response.expiresAt || null,
      provider: ApiInterfaces.ProviderType.LOCAL,
    });
    // Navigate to hub after login
    navigate('/hub');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 sm:px-6 py-12">
      <div className="mb-6">
        <img
          src="/flashgate-logo-black.png"
          alt="Flashgate logo"
          className="h-6 sm:h-8"
          onClick={handleBackToHome}
          style={{ cursor: 'pointer' }}
        />
      </div>

      <div className="w-full max-w-[360px] mx-auto">
        <h2
          className="text-center mb-4"
          style={{
            fontSize: '30px',
            fontWeight: 600,
            lineHeight: '38px',
            letterSpacing: '0%',
          }}
        >
          Log in to your account
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Welcome back! Please enter your details.
        </p>

        <form onSubmit={handleEmailSignIn} className="mb-6">
          {/* Email input */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#617BFF]"
              required
            />
          </div>

          {/* Password input */}
          <div className="mb-1">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#617BFF] pr-10"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Forgot password link */}
          <div className="flex items-center justify-between mb-6">
            <div></div>
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-[#617BFF] hover:text-[#4a62cc]"
            >
              Forgot password
            </Link>
          </div>

          {/* Sign in button */}
          <button
            type="submit"
            className="flex justify-center items-center py-2 w-full border border-transparent rounded-md bg-[#617BFF] text-white hover:bg-[#4a62cc] mb-3"
            style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '16px', lineHeight: '24px' }}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Sign in'}
          </button>
        </form>

        <div className="space-y-3 mb-6">
          {/* Sign in with Google button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
            style={{
              color: '#414651',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
            }}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign in with Google
          </button>

          {/* Sign in with Microsoft button */}
          <button
            type="button"
            onClick={handleMicrosoftSignIn}
            className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
            style={{
              color: '#414651',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
            }}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" viewBox="0 0 23 23">
              <path fill="#f35325" d="M1 1h10v10H1z" />
              <path fill="#81bc06" d="M12 1h10v10H12z" />
              <path fill="#05a6f0" d="M1 12h10v10H1z" />
              <path fill="#ffba08" d="M12 12h10v10H12z" />
            </svg>
            Sign in with Microsoft
          </button>

          {/* Sign in with GitHub button */}
          <button
            type="button"
            onClick={handleGithubSignIn}
            className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
            style={{
              color: '#414651',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
            }}
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path
                fillRule="evenodd"
                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                clipRule="evenodd"
              />
            </svg>
            Sign in with Github
          </button>
        </div>

        {/* Support link */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/auth?tab=signup" className="font-medium text-[#617BFF] hover:text-[#4a62cc]">
              Sign up
            </Link>
          </p>
          <p className="text-sm text-gray-600">
            Trouble signing in?{' '}
            <Link to="/contact" className="font-medium text-gray-900">
              Contact support
            </Link>
          </p>
        </div>
      </div>

      {/* No Account Modal - Shows when user doesn't have an account */}
      {showNoAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#F0F2FF] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                No account found
              </h3>
              <p className="text-gray-600 mb-6">
                It looks like you don't have an account yet. Create one for free, or schedule a demo if you'd like help getting started.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowNoAccountModal(false);
                    navigate('/auth?tab=signup');
                  }}
                  className="w-full px-6 py-3 text-sm font-medium text-white bg-[#617BFF] rounded-lg hover:bg-[#5068E0] transition-colors"
                >
                  Create Free Account
                </button>
                <button
                  onClick={async () => {
                    try {
                      if (apiClient) {
                        const response = await apiClient.requestDemo({
                          userId: '',
                          email: '',
                          name: '',
                        });
                        if (response.success && response.calendarUrl) {
                          window.open(response.calendarUrl, '_blank');
                        }
                      }
                    } catch (error) {
                      console.error('Error requesting demo:', error);
                    }
                    setShowNoAccountModal(false);
                  }}
                  className="w-full px-6 py-3 text-sm font-medium text-[#617BFF] bg-[#F0F2FF] rounded-lg hover:bg-[#E3E8FF] transition-colors"
                >
                  Request a Demo
                </button>
                <button
                  onClick={() => setShowNoAccountModal(false)}
                  className="w-full px-6 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
